import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FeedbackPageView, HostActionView, PreArrivalView, PublicBranding, VisitorLocale } from '@cloudsign/shared';
import { z } from 'zod';
import { ApiClientError, api } from '../../lib/api';
import { t } from '../../lib/i18n';

/**
 * API calls of the public link pages (ADR 0017). Every call carries the signed token in the path and nothing else:
 * no session, no portal endpoint. A 401 never sends the visitor to the sign-in page (quiet401).
 */
export const PRE = '/pre-registration/public';
export const FEEDBACK = '/feedback/public';
export const HOST_ACTION = '/notifications/public/host-action';
export const Unsubscribed = z.object({ unsubscribed: z.literal(true) });

export const preUrl = (token: string, part = '') => `${PRE}/${encodeURIComponent(token)}${part}`;
export const feedbackUrl = (token: string, part = '') => `${FEEDBACK}/${encodeURIComponent(token)}${part}`;
export const hostActionUrl = (token: string) => `${HOST_ACTION}/${encodeURIComponent(token)}`;

export const getPre = (token: string) => api.get(preUrl(token), { schema: PreArrivalView, quiet401: true });
export const getFeedback = (token: string, preselect: number | null) =>
  api.get(feedbackUrl(token), { schema: FeedbackPageView, quiet401: true, ...(preselect ? { query: { r: preselect } } : {}) });
export const getHostAction = (token: string) => api.get(hostActionUrl(token), { schema: HostActionView, quiet401: true });

/** Visitor languages on offer for a public page (GET /links/:token/branding, docs/modules/languages.md s1). Empty `offered` = no picker. */
export const LinkLanguages = z.object({
  default: VisitorLocale,
  offered: z.array(z.object({ code: VisitorLocale, endonym: z.string(), dir: z.enum(['ltr', 'rtl']) })),
});
export type LinkLanguages = z.infer<typeof LinkLanguages>;
/** `languages` is optional so the plain /public/branding answer (no languages) still reads. */
export const LinkBranding = PublicBranding.extend({ languages: LinkLanguages.optional() });
export type LinkBranding = z.infer<typeof LinkBranding>;

/**
 * The tenant's colours, logo and languages for a public page. Link pages pass the signed token (any bad link answers 404,
 * which just means default branding). Pages addressed by tenant slug (forms, privacy) pass `slug` instead.
 */
export function useLinkBranding(token: string, slug?: string) {
  return useQuery({
    queryKey: slug ? ['public-link', 'branding-slug', slug] : ['public-link', 'branding', token],
    queryFn: () =>
      slug
        ? api.get('/public/branding', { query: { tenant: slug }, schema: LinkBranding, quiet401: true })
        : api.get(`/links/${encodeURIComponent(token)}/branding`, { schema: LinkBranding, quiet401: true }),
    enabled: Boolean(slug) || token !== '',
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** Keeps the address (which holds the token) out of Referer headers and search engines while a public page is open. */
export function useNoReferrer(): void {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex';
    document.head.append(meta, robots);
    return () => {
      meta.remove();
      robots.remove();
    };
  }, []);
}

/** 404 (any wrong link) and 410 are the "link not working" page; the rest are ordinary problems. */
export function isInvalidLink(e: unknown): boolean {
  return e instanceof ApiClientError && (e.status === 404 || e.status === 410 || e.messageKey === 'error.link_invalid');
}

export function problemText(e: unknown): string {
  if (e instanceof ApiClientError) {
    if (e.status === 429) return t('publicLinks.rateLimited');
    if (e.fields) return Object.values(e.fields).map((k) => (k.startsWith('error.') ? t(k) : k)).join(' ');
    return t(e.messageKey, e.params);
  }
  return t('error.internal');
}
